import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, Copy, Mail, MessageCircle } from 'lucide-react';

export function VisitDialog({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="visit-dialog">
        <DialogHeader><DialogTitle>Schedule Visit</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Visit Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="visit-date-input" />
            </div>
            <div>
              <Label>Visit Time</Label>
              <Input type="time" value={form.time || ''} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="visit-time-input" />
            </div>
          </div>
          <div>
            <Label>Location *</Label>
            <Input placeholder="e.g., Client Office, 123 Main St" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required data-testid="visit-location-input" />
          </div>
          <div>
            <Label>Purpose</Label>
            <Textarea placeholder="Purpose of the visit..." value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} rows={3} data-testid="visit-purpose-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="visit-save-button">Schedule Visit</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MeetingSheet({ open, onOpenChange, form, setForm, onSubmit, leadId, bookingLink, loadingBookingLink, onGetBookingLink, onCopyLink, onInsertEmail, onSendWhatsApp }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md" data-testid="meeting-sheet">
        <SheetHeader><SheetTitle>Schedule Meeting</SheetTitle></SheetHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4 mt-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Meeting Date *</Label>
              <Input type="date" className="h-10" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="meeting-date-input" />
            </div>
            <div>
              <Label>Time *</Label>
              <Input type="time" className="h-10" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required data-testid="meeting-time-input" />
            </div>
          </div>
          <div>
            <Label>Duration (minutes)</Label>
            <Input type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }} placeholder="e.g., 30" className="h-10" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} min="15" step="15" data-testid="meeting-duration-input" />
          </div>
          <div>
            <Label>Platform</Label>
            <Select value={form.platform} onValueChange={(value) => setForm({ ...form, platform: value })}>
              <SelectTrigger data-testid="meeting-platform-select"><SelectValue placeholder="Select Platform" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="google_meet">Google Meet</SelectItem>
                <SelectItem value="zoom">Zoom</SelectItem>
                <SelectItem value="teams">Microsoft Teams</SelectItem>
                <SelectItem value="face_to_face">Face to Face</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.platform && form.platform !== 'face_to_face' && (
            <div>
              <Label>Meeting Link</Label>
              <Input placeholder="https://meet.google.com/... or https://zoom.us/..." className="h-10" value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} data-testid="meeting-link-input" />
            </div>
          )}
          {leadId && onGetBookingLink && (
            <div className="rounded-lg border border-dashed p-3 space-y-2 bg-muted/30" data-testid="booking-link-block">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1 text-xs font-medium">
                  <Calendar className="h-3.5 w-3.5" /> Google Calendar Booking Link
                </Label>
                {!bookingLink && (
                  <Button type="button" size="sm" variant="outline" className="h-7" onClick={onGetBookingLink} disabled={loadingBookingLink} data-testid="get-booking-link-btn">
                    {loadingBookingLink ? 'Getting…' : 'Get Link'}
                  </Button>
                )}
              </div>
              {bookingLink && (
                <>
                  <Input readOnly value={bookingLink} className="h-9 text-xs" data-testid="booking-link-input" onFocus={(e) => e.target.select()} />
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => onCopyLink(bookingLink)} data-testid="copy-booking-link"><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
                    <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => setForm({ ...form, meeting_link: bookingLink })} data-testid="use-booking-link">Use as link</Button>
                    <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => onInsertEmail(bookingLink)} data-testid="insert-booking-email"><Mail className="h-3.5 w-3.5 mr-1" />Email</Button>
                    <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => onSendWhatsApp(bookingLink)} data-testid="send-booking-whatsapp"><MessageCircle className="h-3.5 w-3.5 mr-1" />WhatsApp</Button>
                  </div>
                </>
              )}
            </div>
          )}
          <div>
            <Label>Agenda</Label>
            <Textarea placeholder="Meeting agenda and topics to discuss..." value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} rows={4} className="min-h-[100px]" data-testid="meeting-agenda-input" />
          </div>
          <Button type="submit" className="w-full" data-testid="meeting-save-button">Schedule Meeting</Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export function MakeCallDialog({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="make-call-dialog">
        <DialogHeader><DialogTitle>Log Call</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
          <div>
            <Label>Call Type</Label>
            <Select value={form.call_type} onValueChange={(value) => setForm({ ...form, call_type: value })}>
              <SelectTrigger data-testid="call-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="outgoing">Outgoing</SelectItem>
                <SelectItem value="incoming">Incoming</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Duration</Label>
            <Input placeholder="e.g., 15 min" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="call-duration-input" />
          </div>
          <div>
            <Label>Notes *</Label>
            <Textarea placeholder="What was discussed..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} required data-testid="call-notes-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="make-call-save-button">Log Call</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CallSummaryDialog({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="call-summary-dialog">
        <DialogHeader><DialogTitle>Call Summary</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
          <div>
            <Label>Call Type</Label>
            <Select value={form.call_type} onValueChange={(value) => setForm({ ...form, call_type: value })}>
              <SelectTrigger data-testid="call-summary-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="outgoing">Outgoing</SelectItem>
                <SelectItem value="incoming">Incoming</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Duration</Label>
            <Input placeholder="e.g., 15 min" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="call-summary-duration-input" />
          </div>
          <div>
            <Label>Summary *</Label>
            <Textarea placeholder="Brief summary of the call..." value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} required data-testid="call-summary-input" />
          </div>
          <div>
            <Label>Detailed Notes</Label>
            <Textarea placeholder="Additional details..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} data-testid="call-summary-notes-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="call-summary-save-button">Save Summary</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditDealDialog({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="edit-deal-dialog">
        <DialogHeader><DialogTitle>Edit Deal</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Deal Title *</Label>
              <Input placeholder="e.g., Q1 Enterprise Deal" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }} placeholder="0.00" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} required step="0.01" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Expected Close Date</Label>
              <Input type="date" value={form.expectedCloseDate} onChange={(e) => setForm({ ...form, expectedCloseDate: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(value) => setForm({ ...form, stage: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospecting">Prospecting</SelectItem>
                  <SelectItem value="qualification">Qualification</SelectItem>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="closed-won">Closed Won</SelectItem>
                  <SelectItem value="closed-lost">Closed Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea placeholder="Deal description..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit">Update Deal</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TaskSheet({ open, onOpenChange, form, setForm, onSubmit }) {
  const today = new Date().toISOString().split('T')[0];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md" data-testid="task-sheet">
        <SheetHeader><SheetTitle>Add Task</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-6">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Task title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Task description" rows={3} />
          </div>
          <div>
            <Label>Due Date</Label>
            <Input type="date" min={today} value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} data-testid="task-due-date" />
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full" onClick={onSubmit}>Create Task</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function FollowupSheet({ open, onOpenChange, form, setForm, onSubmit }) {
  const today = new Date().toISOString().split('T')[0];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md" data-testid="followup-sheet">
        <SheetHeader><SheetTitle>Add Follow-up</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-6">
          <div>
            <Label>Date</Label>
            <Input type="date" min={today} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="followup-date" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Follow-up notes" rows={4} />
          </div>
          <Button className="w-full" onClick={onSubmit}>Create Follow-up</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function EmailSheet({ open, onOpenChange, form, setForm, onSubmit, leadEmail }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md" data-testid="email-sheet">
        <SheetHeader><SheetTitle>Compose Email</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-6">
          <div>
            <Label>To</Label>
            <Input value={leadEmail || ''} disabled className="bg-muted" />
          </div>
          <div>
            <Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Email subject" />
          </div>
          <div>
            <Label>Body</Label>
            <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Email body" rows={6} />
          </div>
          <Button className="w-full" onClick={onSubmit}>Send Email</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function CallSheet({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md" data-testid="call-sheet">
        <SheetHeader><SheetTitle>Log Call</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-6">
          <div>
            <Label>Call Type</Label>
            <Select value={form.call_type} onValueChange={(v) => setForm({ ...form, call_type: v })}>
              <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="outgoing">Outgoing</SelectItem>
                <SelectItem value="incoming">Incoming</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Duration (minutes)</Label>
            <Input type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="Call duration" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Call notes" rows={3} />
          </div>
          <div>
            <Label>Summary</Label>
            <Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Call summary" rows={3} />
          </div>
          <Button className="w-full" onClick={onSubmit}>Log Call</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
